import UserStatusEnum from '@constants/enums/user/status';

import ReportApi from '@packages/reports/api';

import i18n from '@plugins/i18n';

import { Collection, Adviser } from '@/models';

export class InvestorUsersReportCollection extends Collection {
    static type = 'InvestorUsersReportCollection';
    static endpoint = new ReportApi().methods.getInvestorUsers;

    static fields() {
        return {
            ...super.fields(),
            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id')
        };
    }

    get preset_filters() {
        let filters = {};

        if (this.adviser_id) {
            filters.adviser_id = this.filterOperator('is') + ':' + this.adviser_id;
        }

        return filters;
    }

    get default_headers() {
        return [
            'name',
            'status',
            'investor_name',
            'investor_primary_contact_name',
            'can_login',
            'first_login_date',
            'last_login_date'
        ];
    }

    get xlsx_headers() {
        return [
            'id',
            'name',
            'email',
            'status',
            'investor_name',
            'investor_primary_contact_name',
            'investor_primary_contact_email',
            'can_login',
            'first_login_date',
            'last_login_date'
        ];
    }

    get headers() {
        return [
            this.createHeader('id', i18n.t('id'), {
                classList: ['no-wrap'],
                width: '20rem',
                filterable: false,
                sortable: false
            }),
            this.createHeader('name', i18n.t('name'), {
                width: '18rem',
                minWidth: '18rem',
                filterable: {
                    label: i18n.t('user_name')
                },
                sortable: true
            }),
            this.createHeader('email', i18n.t('email'), {
                width: '15rem',
                filterable: true,
                sortable: true,
                active: true
            }),
            this.createHeader('status', i18n.t('status'), {
                filterable: true,
                sortable: true,
                width: '8rem',
                format: 'enum',
                enum: UserStatusEnum,
                enumA11yKey: 'enums.user_status',
                enumTypes: {
                    [UserStatusEnum.ACTIVE]: 'success',
                    [UserStatusEnum.INACTIVE]: 'grey',
                    [UserStatusEnum.SUSPENDED]: 'error',
                    [UserStatusEnum.UNVERIFIED]: 'warning'
                }
            }),
            this.createHeader('investor_name', i18n.tc('investor', 1), {
                width: '18rem',
                minWidth: '18rem',
                filterable: true,
                sortable: true
            }),
            this.createHeader('primary_contact_id', i18n.t('primary_contact'), {
                active: false,
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/User.vue'),
                    disabled: () => !this.adviser_id,
                    filtersFromContext: () => {
                        if (!this.adviser_id) {
                            return {};
                        }

                        return {
                            role_in_organisation: this.adviser_id,
                            primary_contact: `${this.filterOperator('is')}:true`
                        };
                    }
                }
            }),
            this.createHeader('investor_primary_contact_name', i18n.t('primary_contact'), {
                width: '18rem',
                minWidth: '18rem',
                filterable: false,
                sortable: false
            }),
            this.createHeader('investor_primary_contact_email', i18n.t('primary_contact_email'), {
                width: '18rem',
                filterable: false,
                sortable: false,
                active: false
            }),
            this.createHeader('can_login', i18n.t('login_enabled'), {
                format: 'boolean',
                width: '10rem',
                filterable: true,
                sortable: true
            }),
            this.createHeader('first_login_date', i18n.t('first_logged_in_at'), {
                format: 'date',
                width: '12rem',
                align: 'right',
                filterable: true,
                sortable: true
            }),
            this.createHeader('last_login_date', i18n.t('last_logged_in_at'), {
                format: 'date',
                width: '12rem',
                align: 'right',
                filterable: true,
                sortable: true
            })
        ];
    }
}

export default InvestorUsersReportCollection;
